import React, { useContext, useEffect } from 'react';
import proptypes from 'prop-types';
import classNames from 'classnames/bind';
import { useStore } from '@thd-nucleus/experience-context';
import { Alert } from '@one-thd/sui-atomic-components';
import { StoreSearchContext } from '../StoreSearch.context';
import { Pod } from './Pod';
import { SuggestedAddresses } from './SuggestedAddresses';

const {
  bool, number, oneOf, string, func
} = proptypes;

const cx = classNames.bind();

const error = {
  title: 'We\'re Sorry,',
  message: 'but we cannot find a Home Depot Store that meets your search criteria.'
};

export const Results = ({
  disableScroll = false,
  linkType = 'store',
  maxNumberOfResults = 20,
  omitStoreNumber = '',
  hostName = '',
  hasBadge = true,
  showAllStoreLocalizeButtons = false,
  customStoreHandler = () => {}
}) => {

  const { storeId: localizedStoreId } = useStore();

  const {
    showError,
    showLoader,
    suggestedAddresses,
    stores = []
  } = useContext(StoreSearchContext);

  const getFilteredStores = () => {
    const storeNumberToOmit = omitStoreNumber.length < 4 ? '0' + omitStoreNumber : omitStoreNumber;
    return (stores.filter((store) => store.storeId !== storeNumberToOmit)).slice(0, maxNumberOfResults);
  };

  let resultsType = 'stores';
  if (suggestedAddresses) {
    resultsType = 'suggestedAddresses';
  } else if (showLoader) {
    resultsType = 'loader';
  } else if (showError) {
    resultsType = 'error';
  }

  const resultsClassNames = cx(
    'sui-relative box-border sui-flex sui-flex-col sui-overflow-hidden sui-w-full', {
      'sui-h-[645px] sui-overflow-y-auto sui-mr-1-5': !disableScroll,
      'sui-items-center sui-pt-4': showLoader,
      'sui-bg-primary sui-px-0 sui-py-3': showError || suggestedAddresses
    });

  return (
    <div
      className={resultsClassNames}
      id="js-results"
      data-component="Results"
    >
      {resultsType === 'stores' && (
        <>
          {getFilteredStores().map((store, index) => (
            <Pod
              disableScroll={disableScroll}
              index={index + 1}
              isLocalizedStore={+store?.storeId === +localizedStoreId}
              key={store.storeId}
              linkType={linkType}
              store={store}
              hostName={hostName}
              hasBadge={hasBadge}
              showAllStoreLocalizeButtons={showAllStoreLocalizeButtons}
              customStoreHandler={customStoreHandler}
            />
          ))}
        </>
      )}
      {resultsType === 'suggestedAddresses' && (
        <SuggestedAddresses stores={suggestedAddresses} />
      )}
      {resultsType === 'error' && (
        <div className="sui-p-3">
          <Alert status="error">
            {error?.title + ' ' + error?.message}
          </Alert>
        </div>
      )}
      {resultsType === 'loader' && (
        <div className="sui-animate-pulse">
          <div className="sui-w-auto sui-bg-strong sui-rounded-lg" />
        </div>
      )}
    </div>
  );
};

Results.propTypes = {
  disableScroll: bool,
  linkType: oneOf(['directions', 'localAd', 'rentals', 'services', 'store', 'gardenCenter', 'proDesk']),
  maxNumberOfResults: number,
  omitStoreNumber: string,
  hostName: string,
  hasBadge: bool,
  // `showAllStoreLocalizeButtons` will cause the Store Pod to display the "Shop in this Store" button, even
  // if the user is already localized to this store on homedepot.com.  This is a tactical
  // fix for pro-assist.homedepot.com which was unable to select a store that was already the
  // localized store on B2B
  showAllStoreLocalizeButtons: bool,
  customStoreHandler: func
};

Results.displayName = 'Results';
